import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate, fmtDateRel } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
    Calendar, CheckCircle2, Clock, AlertCircle, Zap, Tag, TrendingDown,
    ArrowDownCircle, ArrowUpCircle, FileWarning, Repeat, Phone, MessageSquare,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useI18n } from "../../lib/i18n";

const PRODUCT_COLORS = {
    NanoCredit: "#00D084", FlexCredit: "#0066FF", SalaryAdvance: "#FF6B00",
    SplitPay: "#7000FF", PrimeCredit: "#FF0055", BizCredit: "#4F46E5",
    TradeCredit: "#06B6D4", StockFinance: "#F59E0B", AgroCredit: "#00D084",
    FleetFinance: "#7000FF",
};

const STATUS_META = {
    paid: { key: "loan.status_paid", color: "#00D084", icon: CheckCircle2, bg: "bg-emerald-50", text: "text-emerald-700" },
    upcoming: { key: "loan.status_upcoming", color: "#0066FF", icon: Clock, bg: "bg-blue-50", text: "text-blue-700" },
    partial: { key: "loan.status_partial", color: "#FF6B00", icon: AlertCircle, bg: "bg-amber-50", text: "text-amber-700" },
    overdue: { key: "loan.status_overdue", color: "#FF0055", icon: AlertCircle, bg: "bg-rose-50", text: "text-rose-700" },
};

const EVENT_META = {
    disbursed: { key: "loan.event.disbursed", icon: ArrowDownCircle, color: "#00D084" },
    repaid: { key: "loan.event.repaid", icon: CheckCircle2, color: "#00D084" },
    partial_repaid: { key: "loan.event.partial_repaid", icon: ArrowUpCircle, color: "#0066FF" },
    auto_debited: { key: "loan.event.auto_debited", icon: Zap, color: "#FF6B00" },
    late_fee: { key: "loan.event.late_fee", icon: TrendingDown, color: "#FF0055" },
    overdue: { key: "loan.event.overdue", icon: FileWarning, color: "#FF0055" },
    early_repaid: { key: "loan.event.early_repaid", icon: Zap, color: "#7000FF" },
    restructured: { key: "loan.event.restructured", icon: Repeat, color: "#0066FF" },
    contacted: { key: "loan.event.contacted", icon: Phone, color: "#06B6D4" },
};

export default function LoanDetail() {
    const { loanId } = useParams();
    const nav = useNavigate();
    const { t } = useI18n();
    const [loan, setLoan] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [statement, setStatement] = useState(null);
    const [tab, setTab] = useState("schedule");
    const [busy, setBusy] = useState(false);
    const [earlyOpen, setEarlyOpen] = useState(false);
    const [earlyPreview, setEarlyPreview] = useState(null);

    const load = async () => {
        const [l, s, st] = await Promise.all([
            api.get(`/loans/${loanId}`),
            api.get(`/loans/${loanId}/schedule`),
            api.get(`/loans/${loanId}/statement`),
        ]);
        setLoan(l.data);
        setSchedule(s.data);
        setStatement(st.data);
    };

    useEffect(() => { load(); }, [loanId]);

    if (!loan || !schedule || !statement) {
        return <div className="p-8 text-center text-slate-500">Loading…</div>;
    }

    const color = PRODUCT_COLORS[loan.product] || "#0066FF";
    const outstanding = statement.outstanding;
    const progress = Math.min(100, ((loan.repaid_amount || 0) / loan.total_repayment) * 100);
    const isClosed = loan.status === "repaid";

    const payInstallment = async (n) => {
        setBusy(true);
        try {
            await api.post("/loans/installments/pay", { loan_id: loanId, installment_n: n });
            toast.success(`Installment #${n} paid`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment failed");
        } finally {
            setBusy(false);
        }
    };

    const payInFull = async () => {
        setBusy(true);
        try {
            await api.post("/loans/repay", { loan_id: loanId, amount: outstanding });
            toast.success("Loan repaid in full");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Repayment failed");
        } finally {
            setBusy(false);
        }
    };

    const previewEarlyRepay = () => {
        // Local calc preview based on tenure elapsed
        const disbursed = new Date(loan.disbursed_at).getTime();
        const tenureMs = loan.tenure_days * 86400000;
        const elapsed = Math.max(0, (Date.now() - disbursed) / tenureMs);
        const frac = Math.min(1, elapsed);
        const accruedInt = loan.interest * frac;
        const unaccrued = Math.max(0, loan.interest - accruedInt);
        const discount = unaccrued * 0.5;
        const settlement = Math.max(0, loan.amount + accruedInt + (unaccrued - discount) + loan.fees + (loan.late_fee_total || 0) - (loan.repaid_amount || 0));
        setEarlyPreview({ accruedInt, unaccrued, discount, settlement });
        setEarlyOpen(true);
    };

    const confirmEarlyRepay = async () => {
        setBusy(true);
        try {
            const r = await api.post("/loans/early-repay", { loan_id: loanId });
            toast.success(t("loan.early.toast_saved", { amount: fmtCurrency(r.data.discount) }));
            setEarlyOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="loan-detail-page">
            <MobileHeader title={t("loan.details")} backTo={-1} />
            <div className="px-5 -mt-6 pb-4 space-y-4">
                {/* Summary card */}
                <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: color }} data-testid="loan-summary-card">
                    <div className="flex justify-between items-start gap-3">
                        <div>
                            <div className="font-display text-lg font-semibold">{loan.product}™</div>
                            <div className="text-xs text-white/80 mt-1 uppercase tracking-wider">{loan.status === "active" ? "Active" : loan.status} loan</div>
                        </div>
                        <span className="text-[10px] px-2 py-1 rounded-md bg-white/15 backdrop-blur font-bold">
                            {t("loan.tenure_days", { n: loan.tenure_days })}
                        </span>
                    </div>
                    <div className="font-display text-3xl font-bold mt-3">{fmtCurrency(loan.amount)}</div>
                    <div className="text-xs text-white/80">Loan ID: {loan.id.slice(0, 12).toUpperCase()}</div>

                    {/* Outstanding + progress */}
                    <div className="mt-5 bg-white/15 backdrop-blur rounded-xl p-3">
                        <div className="flex justify-between text-xs">
                            <span className="text-white/80">{t("loan.outstanding")}</span>
                            <span className="font-bold">{fmtCurrency(outstanding)}</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full rounded-full bg-white" style={{ width: `${progress}%` }} />
                        </div>
                        <div className="flex justify-between text-[10px] text-white/70 mt-1">
                            <span>{fmtCurrency(loan.repaid_amount || 0)} {t("loan.repaid")}</span>
                            <span>{fmtCurrency(loan.total_repayment)} {t("loan.total")}</span>
                        </div>
                    </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2">
                    <Stat label={t("loan.due_on")} value={fmtDate(loan.due_date)} />
                    <Stat label={t("loan.disbursed_on")} value={fmtDate(loan.disbursed_at)} />
                    <Stat label={t("loan.late_fees")} value={fmtCurrency(loan.late_fee_total || 0)} alert={(loan.late_fee_total || 0) > 0} />
                </div>

                {/* Actions */}
                {!isClosed && (
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            onClick={payInFull}
                            disabled={busy}
                            className="h-12 rounded-xl font-semibold"
                            style={{ background: color }}
                            data-testid="repay-full-btn"
                        >
                            {t("loan.repay_full", { amount: fmtCurrency(outstanding, "NGN", { compact: true }) })}
                        </Button>
                        <Button
                            onClick={previewEarlyRepay}
                            disabled={busy}
                            variant="outline"
                            className="h-12 rounded-xl font-semibold border-2"
                            data-testid="early-repay-btn"
                        >
                            <Zap size={16} /> {t("loan.early_settle")}
                        </Button>
                    </div>
                )}

                {/* Tabs: schedule | statement */}
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-2 w-full" data-testid="loan-tabs">
                        <TabsTrigger value="schedule" data-testid="tab-schedule">
                            <Calendar size={14} /> {t("loan.schedule")} ({schedule.items.length})
                        </TabsTrigger>
                        <TabsTrigger value="statement" data-testid="tab-statement">
                            <Tag size={14} /> {t("loan.statement")} ({statement.events.length})
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {tab === "schedule" && (
                    <div className="space-y-2" data-testid="schedule-list">
                        {schedule.items.map((it) => {
                            const meta = STATUS_META[it.status] || STATUS_META.upcoming;
                            const Icon = meta.icon;
                            const remaining = Math.max(0, it.amount - (it.paid_amount || 0));
                            return (
                                <div key={it.n} className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`installment-${it.n}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`size-11 rounded-xl flex items-center justify-center ${meta.bg} ${meta.text}`}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-semibold text-[#0A1930]">
                                                {schedule.items.length > 1
                                                    ? t("loan.installment_n_of", { n: it.n, total: schedule.items.length })
                                                    : t("loan.installment_n", { n: it.n })}
                                            </div>
                                            <div className="text-xs text-slate-500">{t("loan.installment_due", { date: fmtDate(it.due_date) })}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display font-bold">{fmtCurrency(it.amount)}</div>
                                            <div className={`text-[10px] uppercase font-bold tracking-wider`} style={{ color: meta.color }}>
                                                {t(meta.key)}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Breakdown */}
                                    <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs">
                                        <Mini label={t("loan.principal")} value={fmtCurrency(it.principal, "NGN", { compact: true })} />
                                        <Mini label={t("loan.interest")} value={fmtCurrency(it.interest, "NGN", { compact: true })} />
                                        <Mini label={t("loan.fees")} value={fmtCurrency(it.fees, "NGN", { compact: true })} />
                                    </div>
                                    {/* Action */}
                                    {it.status !== "paid" && !isClosed && remaining > 0 && (
                                        <Button
                                            onClick={() => payInstallment(it.n)}
                                            disabled={busy}
                                            size="sm"
                                            className="w-full mt-3 h-9 rounded-lg text-xs font-semibold"
                                            style={{ background: color }}
                                            data-testid={`pay-installment-${it.n}`}
                                        >
                                            {t("loan.pay_installment", { amount: fmtCurrency(remaining) })}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {tab === "statement" && (
                    <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden" data-testid="statement-list">
                        {statement.events.length === 0 && (
                            <div className="p-8 text-center text-sm text-slate-500">{t("loan.no_events")}</div>
                        )}
                        {statement.events.map((e, i) => {
                            const m = EVENT_META[e.kind] || { key: e.kind, icon: AlertCircle, color: "#64748B" };
                            const Icon = m.icon;
                            return (
                                <div key={e.id || i} className={`p-4 flex items-start gap-3 ${i !== statement.events.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`event-${e.kind}-${i}`}>
                                    <div className="size-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${m.color}15`, color: m.color }}>
                                        <Icon size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm">{m.key.startsWith("loan.event.") ? t(m.key) : e.kind}</div>
                                        {e.description && <div className="text-xs text-slate-600 mt-0.5">{e.description}</div>}
                                        <div className="text-[10px] text-slate-400 mt-1">{fmtDateRel(e.created_at)} · {e.actor || "system"}</div>
                                    </div>
                                    {e.amount > 0 && (
                                        <div className="font-mono font-bold text-sm" style={{ color: m.color }}>
                                            {fmtCurrency(e.amount, "NGN", { compact: true })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Early repay confirmation */}
            <Dialog open={earlyOpen} onOpenChange={setEarlyOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2"><Zap size={18} className="text-[#7000FF]" /> {t("loan.early.title")}</DialogTitle>
                        <DialogDescription>{t("loan.early.desc")}</DialogDescription>
                    </DialogHeader>
                    {earlyPreview && (
                        <div className="space-y-3">
                            <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-slate-500">{t("loan.early.accrued")}</span><span className="font-semibold">{fmtCurrency(earlyPreview.accruedInt)}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">{t("loan.early.unaccrued")}</span><span className="font-semibold">{fmtCurrency(earlyPreview.unaccrued)}</span></div>
                                <div className="flex justify-between text-emerald-600">
                                    <span className="font-semibold">{t("loan.early.discount")}</span>
                                    <span className="font-bold">−{fmtCurrency(earlyPreview.discount)}</span>
                                </div>
                                <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between">
                                    <span className="font-display font-semibold text-[#0A1930]">{t("loan.early.settlement")}</span>
                                    <span className="font-display text-lg font-bold">{fmtCurrency(earlyPreview.settlement)}</span>
                                </div>
                            </div>
                            <Button onClick={confirmEarlyRepay} disabled={busy} className="w-full h-11 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6]" data-testid="confirm-early-btn">
                                {busy ? t("loan.early.processing") : t("loan.early.confirm", { amount: fmtCurrency(earlyPreview.discount) })}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, value, alert }) {
    return (
        <div className={`bg-white rounded-xl border ${alert ? "border-rose-200 bg-rose-50/50" : "border-slate-200/60"} p-3`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
            <div className={`text-xs font-bold mt-1 ${alert ? "text-rose-600" : "text-[#0A1930]"}`}>{value}</div>
        </div>
    );
}

function Mini({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">{label}</div>
            <div className="font-mono text-xs font-bold">{value}</div>
        </div>
    );
}
